import React, { useState, useEffect } from 'react'
import { formatNumberWithCommas, removeCommas, months, days, abrMonths } from './Helpers'
import Pin from '../../images/icons/Pin'
import ArrowDownExpand from '../../images/icons/ArrowDownExpand'
import { CustomSelect } from '@strata-monorepo/shared-ui/components/atoms/CustomSelect/index'
import { getSmartyAutoComplete } from '@strata-monorepo/shared-ui/utils/smartyStreetsService'
import type { BillEntryData } from './BillEntry'
import useDebounce from '@strata-monorepo/shared-ui/utils/useDebounce'
import { formatAddress } from '@strata-monorepo/shared-ui/utils/formatAddress'
import type { Address } from '@strata-monorepo/shared-ui/types/Address'
import { cn } from '@strata-monorepo/shared-ui/utils/cn'

/** Form controls omitted from the bill entry form when listed in `disabledFields`. */
export type EntryFormDisabledField = 'planName' | 'day'

interface EntryFormProps {
  entry: BillEntryData
  entries: BillEntryData[]
  allEntries?: BillEntryData[]
  onChange?: (entries: BillEntryData[]) => void
  isMobile?: boolean
  index: number
  onCancelEdit?: () => void
  isEditing?: boolean
  requiredFields: readonly string[]
  onSubmit?: (billData: BillEntryData, fieldSource: string) => void
  type: 'upload' | 'manual'
  disabledFields?: readonly EntryFormDisabledField[]
}

export default function EntryForm({
  entries,
  allEntries,
  onChange,
  isMobile,
  index,
  entry,
  onCancelEdit,
  isEditing = false,
  requiredFields,
  onSubmit,
  type,
  disabledFields = [],
}: EntryFormProps): React.ReactElement {
  const isFormFieldHidden = (field: EntryFormDisabledField): boolean =>
    disabledFields.includes(field)
  const [invalidUsage, setInvalidUsage] = useState(false)
  const [invalidBilling, setInvalidBilling] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [options, setOptions] = useState<{ value: Address; label: string }[]>([])

  // Pulls in the address from a previous entry to avoid having to re-enter it
  const defaultAddress = allEntries?.find((e) => e.address && e.address !== '')?.address
  const [editedEntry, setEditedEntry] = useState({
    ...entry,
    address: entry.address || defaultAddress || '',
  })
  const [addressInput, setAddressInput] = useState(entry.address || defaultAddress || '')

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 10 }, (_, i) => (currentYear - i).toString())

  const selectTriggerClassName =
    'strata:bg-white strata:border strata:border-secondary-500 strata:p-4 strata:text-sm strata:font-semibold strata:text-secondary-500 strata:rounded-sm strata:focus-visible:focus-strata-inner strata:cursor-pointer strata:flex strata:items-center strata:justify-between strata:text-left strata:truncate'

  // Using a debounced value to prevent too many API calls
  const debouncedInputValue = useDebounce(addressInput, 500) || ''

  useEffect(() => {
    if (!showSuggestions) return
    const handleSmartyLookup = async (val: string) => {
      if (showSuggestions && val.length > 3 && debouncedInputValue) {
        const suggestions = await getSmartyAutoComplete(val)
        const formattedOptions = suggestions.map((address: Address) => ({
          value: address,
          label: formatAddress(address),
        }))
        setOptions(formattedOptions || [])
      } else {
        setOptions([])
      }
    }
    handleSmartyLookup(debouncedInputValue)
  }, [debouncedInputValue, showSuggestions])

  const updateEntry = (id: number, field: keyof BillEntryData, value: string | number): void => {
    let convertedValue: string | number | null = value

    if (field === 'usage' || field === 'billing' || field === 'year') {
      const trimmedValue = value?.toString().trim()
      if (!trimmedValue || trimmedValue === '') {
        convertedValue = null
      } else {
        const numValue = Number(trimmedValue)
        convertedValue = isNaN(numValue) ? null : numValue
      }
    }
    const updatedEntry = { ...editedEntry, [field]: convertedValue }
    setEditedEntry(updatedEntry)
  }

  const updateStoreAddress = (address: string): void => {
    updateEntry(index, 'address', address)
    setAddressInput(address)
    setShowSuggestions(false)
  }

  const removeEntry = (id: number): void => {
    if (entries.length > 1) {
      const updatedEntries = entries.filter((_, index) => index !== id)
      if (onChange) {
        onChange(updatedEntries)
      }
    } else if (entries.length === 1) {
      if (onChange) {
        onChange([])
      }
    }
  }

  const handleSubmit = (index: number, e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Check both usage and billing validation
    const isUsageInvalid = Number(editedEntry.usage) < 250 || Number(editedEntry.usage) > 8000
    const isBillingInvalid =
      editedEntry.billing &&
      (Number(editedEntry.billing) < 20 || Number(editedEntry.billing) > 2000)

    setInvalidUsage(isUsageInvalid)
    setInvalidBilling(!!isBillingInvalid)

    // Only proceed if both are valid
    if (isUsageInvalid || isBillingInvalid) {
      return
    }

    // Call tracking callback with original entry
    if (onSubmit) onSubmit(editedEntry, 'manual')

    // Store the original entry with full plan name
    const updatedEntries = entries.map((entry, i) =>
      i === index ? { ...editedEntry, isSubmitted: true, isEditing: false } : entry,
    )

    if (onChange) {
      onChange(updatedEntries)
    }
  }

  const handleCancel = () => {
    if (isEditing && onCancelEdit) {
      onCancelEdit()
    } else {
      removeEntry(index)
    }
  }

  const submitDisabled = !editedEntry.month || !editedEntry.year || !editedEntry.usage

  const isFieldRequired = (field: string): boolean => requiredFields.includes(field)

  return (
    <form
      onSubmit={(e) => handleSubmit(index, e)}
      className='strata:flex strata:flex-col strata:gap-2'
      data-testid={`entry-form-${type}-${index}`}
    >
      {/* Address */}
      <div className='strata:w-full strata:relative'>
        <label
          htmlFor={`address-${type}-${index}`}
          className='strata:block strata:pb-0.5 strata:@qm/qc:pb-1 strata:text-left strata:text-gray-600 strata:text-xs'
        >
          Address
          {isFieldRequired('address') && (
            <span
              data-testid={`address-required-${type}-${index}`}
              className='strata:text-red-500 strata:text-xs strata:leading-4'
            >
              {' '}
              *
            </span>
          )}
        </label>
        <div className='strata:relative strata:w-full'>
          <Pin classNames='strata:absolute strata:top-4.5 strata:left-3.5 strata:-translate-y-1/2 strata:text-secondary-200' />
          <input
            id={`address-${type}-${index}`}
            type='text'
            value={addressInput}
            placeholder={isMobile ? 'Enter address' : 'Enter your address'}
            autoComplete='off'
            onChange={(e) => {
              setShowSuggestions(true)
              setAddressInput(e.target.value)
            }}
            className='strata:border-gray-400 strata:bg-white strata:placeholder:text-gray-600 strata:placeholder:font-normal strata:border strata:w-full strata:px-3 strata:pl-8 strata:py-2 strata:text-sm strata:text-gray-700 strata:font-bold strata:rounded-sm strata:hover:focus-strata-thin strata:focus-visible:focus-strata-inner strata:h-[36px]'
            data-testid={`address-input-${type}-${index}`}
          />
        </div>
        {showSuggestions && options.length > 0 && (
          <ul className='strata:absolute strata:w-full strata:z-[9999] strata:border strata:border-gray-200 strata:rounded-sm strata:p-1 strata:bg-white'>
            {options.map((option, idx) => (
              <li
                key={option.label}
                className='strata:list-none'
              >
                <button
                  type='button'
                  onClick={() => updateStoreAddress(option.label)}
                  className='strata:w-full strata:cursor-pointer strata:text-left strata:text-body-sm strata:p-2 strata:rounded-sm strata:hover:bg-secondary-50 strata:hover:font-bold'
                  aria-label={`Select address ${option.label}`}
                  data-testid={`address-suggestion-${idx}`}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className='strata:flex strata:flex-wrap strata:@qm/qc:flex-nowrap strata:gap-3 strata:@qm/qc:gap-4 strata:w-full'>
        {/* Usage amount */}
        <div className='strata:flex-1 strata:basis-[24px] strata:@qm/qc:basis-auto'>
          <label
            htmlFor={`usage-${type}-${index}`}
            className='strata:block strata:pb-0.5 strata:@qm/qc:pb-1 strata:text-left strata:text-gray-600 strata:text-xs'
          >
            Usage
            {isFieldRequired('usage') && (
              <span
                data-testid={`usage-required-${type}-${index}`}
                className='strata:text-red-500 strata:text-xs strata:leading-4'
              >
                {' '}
                *
              </span>
            )}
          </label>
          <div className='strata:relative strata:w-full'>
            <input
              id={`usage-${type}-${index}`}
              type='text'
              inputMode='numeric'
              pattern='[0-9,]*'
              value={formatNumberWithCommas(editedEntry.usage)}
              placeholder='Enter usage'
              onChange={(e) => updateEntry(index, 'usage', removeCommas(e.target.value))}
              className={cn(
                'strata:border-gray-400 strata:bg-white strata:placeholder:text-gray-600 strata:placeholder:font-normal strata:border strata:w-full strata:px-4 strata:pr-8 strata:py-3 strata:text-sm strata:font-bold strata:text-gray-700 strata:rounded-sm strata:h-[36px]',
                'strata:autofill:bg-white strata:autofill:shadow-[0_0_0_30px_white_inset]',
                invalidUsage
                  ? 'strata:border-system-negative-accent strata:text-system-negative-foreground strata:focus-visible:outline strata:focus-visible:outline-1 strata:focus-visible:outline-destructive strata:focus-visible:outline-offset-[-1px]'
                  : 'strata:hover:focus-strata-thin strata:focus-visible:focus-strata-inner',
              )}
              data-testid={`usage-input-${type}-${index}`}
            />
            <span className='strata:absolute strata:right-3 strata:top-4.5 strata:transform strata:-translate-y-1/2 strata:text-sm strata:font-semibold strata:text-gray-700'>
              kWh
            </span>
            {invalidUsage && (
              <span
                className='strata:text-left strata:block strata:text-xs strata:text-system-negative-foreground strata:leading-4 strata:break-words'
                data-testid={`usage-error-${type}-${index}`}
              >
                Enter a valid usage from <br />
                250kWh-8,000kWh
              </span>
            )}
          </div>
        </div>

        {/* Bill Amount */}
        <div className='strata:flex-1 strata:basis-[24px] strata:@qm/qc:basis-auto'>
          <label
            htmlFor={`billing-${type}-${index}`}
            className='strata:block strata:pb-0.5 strata:@qm/qc:pb-1 strata:text-left strata:text-gray-600 strata:text-xs'
          >
            Bill
            {isFieldRequired('billing') && (
              <span
                data-testid={`billing-required-${type}-${index}`}
                className='strata:text-red-500 strata:text-xs strata:leading-4'
              >
                {' '}
                *
              </span>
            )}
          </label>
          <div className='strata:relative strata:w-full'>
            <span className='strata:absolute strata:left-3 strata:top-4.5 strata:transform strata:-translate-y-1/2 strata:text-sm strata:font-semibold strata:text-gray-700'>
              $
            </span>
            <input
              id={`billing-${type}-${index}`}
              type='text'
              inputMode='numeric'
              pattern='[0-9,]*'
              value={formatNumberWithCommas(editedEntry.billing)}
              placeholder='Enter amount'
              onChange={(e) => updateEntry(index, 'billing', removeCommas(e.target.value))}
              className={cn(
                'strata:border-gray-400 strata:bg-white strata:placeholder:text-gray-600 strata:placeholder:font-normal strata:border strata:w-full strata:px-3 strata:pl-6 strata:py-2 strata:text-sm strata:font-semibold strata:text-gray-700 strata:rounded-sm strata:h-[36px]',
                'strata:autofill:bg-white strata:autofill:shadow-[0_0_0_30px_white_inset]',
                invalidBilling
                  ? 'strata:border-system-negative-accent strata:text-system-negative-foreground strata:focus-visible:outline strata:focus-visible:outline-1 strata:focus-visible:outline-destructive strata:focus-visible:outline-offset-[-1px]'
                  : 'strata:hover:focus-strata-thin strata:focus-visible:focus-strata-inner',
              )}
              data-testid={`billing-input-${type}-${index}`}
            />
            {invalidBilling && (
              <span
                className='strata:text-left strata:block strata:text-xs strata:text-system-negative-foreground strata:leading-4 strata:break-words strata:max-w-full'
                data-testid={`billing-error-${type}-${index}`}
              >
                Please enter a valid <br />
                amount from $20-$2,000
              </span>
            )}
          </div>
        </div>

        {!isFormFieldHidden('planName') ? (
          <div className='strata:flex-1 strata:basis-full strata:@qm/qc:basis-auto'>
            <label
              htmlFor={`planName-${type}-${index}`}
              className='strata:block strata:pb-0.5 strata:@qm/qc:pb-1 strata:text-left strata:text-gray-600 strata:text-xs'
            >
              Plan name
              {isFieldRequired('planName') && (
                <span
                  data-testid={`planName-required-${type}-${index}`}
                  className='strata:text-red-500 strata:text-xs strata:leading-4'
                >
                  {' '}
                  *
                </span>
              )}
            </label>
            <div className='strata:relative strata:w-full'>
              <input
                id={`planName-${type}-${index}`}
                type='text'
                value={editedEntry.planName}
                placeholder='Enter plan name'
                onChange={(e) => updateEntry(index, 'planName', e.target.value)}
                className='strata:border-gray-400 strata:bg-white strata:placeholder:text-gray-600 strata:placeholder:font-normal strata:border strata:w-full strata:px-4 strata:pr-8 strata:py-3 strata:text-sm strata:font-bold strata:text-gray-700 strata:rounded-sm strata:hover:focus-strata-thin strata:focus-visible:focus-strata-inner strata:h-[36px]'
                data-testid={`planName-input-${type}-${index}`}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className='strata:flex strata:flex-col strata:xs:flex-row strata:gap-2 strata:xs:gap-4 strata:w-full'>
        {/* Month */}
        <div className='strata:w-full'>
          <label
            htmlFor={`month-${type}-${index}`}
            className='strata:block strata:pb-0.5 strata:@qm/qc:pb-1 strata:text-left strata:text-gray-600 strata:text-xs'
          >
            Month
            {isFieldRequired('month') && (
              <span
                data-testid={`month-required-${type}-${index}`}
                className='strata:text-red-500 strata:text-xs strata:leading-4'
              >
                {' '}
                *
              </span>
            )}
          </label>
          <CustomSelect
            placeholder={isMobile ? 'Select' : 'Select month'}
            className='strata:text-slate-600 strata:w-full strata:h-[36px]'
            triggerClassName={selectTriggerClassName}
            triggerTextAlignment='left'
            options={isMobile ? abrMonths : months}
            value={editedEntry.month}
            onChange={(value) => updateEntry(index, 'month', value)}
            data-testid={`month-select-${type}-${index}`}
            dropdownIcon={<ArrowDownExpand classNames='strata:text-secondary-500' />}
          />
        </div>

        {!isFormFieldHidden('day') ? (
          <div className='strata:w-full'>
            <label
              htmlFor={`day-${type}-${index}`}
              className='strata:block strata:pb-0.5 strata:@qm/qc:pb-1 strata:text-left strata:text-gray-600 strata:text-xs'
            >
              Day
              {isFieldRequired('day') && (
                <span
                  data-testid={`day-required-${type}-${index}`}
                  className='strata:text-red-500 strata:text-xs strata:leading-4'
                >
                  {' '}
                  *
                </span>
              )}
            </label>
            <CustomSelect
              placeholder={isMobile ? 'Select' : 'Select day'}
              className='strata:text-slate-600 strata:w-full strata:h-[36px]'
              triggerClassName={selectTriggerClassName}
              triggerTextAlignment='left'
              options={days.map((day) => ({ ...day, label: day.value }))}
              value={String(editedEntry.day)}
              onChange={(value) => updateEntry(index, 'day', Number(value))}
              data-testid={`day-select-${type}-${index}`}
              dropdownIcon={<ArrowDownExpand classNames='strata:text-secondary-500' />}
            />
          </div>
        ) : null}

        {/* Year */}
        <div className='strata:w-full'>
          <label
            htmlFor={`year-${type}-${index}`}
            className='strata:block strata:pb-0.5 strata:@qm/qc:pb-1 strata:text-left strata:text-gray-600 strata:text-xs'
          >
            Year
            {isFieldRequired('year') && (
              <span
                data-testid={`year-required-${type}-${index}`}
                className='strata:text-red-500 strata:text-xs strata:leading-4'
              >
                {' '}
                *
              </span>
            )}
          </label>
          <CustomSelect
            placeholder={isMobile ? 'Select' : 'Select year'}
            className='strata:text-slate-600 strata:w-full strata:h-[36px]'
            triggerClassName={selectTriggerClassName}
            triggerTextAlignment='left'
            options={years.map((year) => ({ key: year, value: year, label: year }))}
            value={editedEntry.year ? String(editedEntry.year) : ''}
            onChange={(value) => updateEntry(index, 'year', Number(value) || 0)}
            data-testid={`year-select-${type}-${index}`}
            dropdownIcon={<ArrowDownExpand classNames='strata:text-secondary-500' />}
          />
        </div>
      </div>

      <div className='strata:mt-1 strata:flex strata:gap-6 strata:w-full strata:@qm/qc:justify-end'>
        <button
          type='button'
          className='strata:md:w-max strata:w-full strata:@qm/qc:px-0 strata:cursor-pointer strata:w-full strata:px-4 strata:py-2 strata:font-bold strata:text-sm strata:leading-5 strata:text-primary-500'
          onClick={handleCancel}
          data-testid={`cancel-button-${type}-${index}`}
        >
          Cancel
        </button>
        <button
          type='submit'
          disabled={submitDisabled}
          className='strata:cursor-pointer strata:w-full strata:@qm/qc:w-max strata:bg-primary-500 strata:text-white strata:font-bold strata:text-sm strata:leading-5 strata:disabled:bg-gray-300 strata:px-4 strata:py-2 strata:rounded-button-primary'
          data-testid={`submit-button-${type}-${index}`}
        >
          {isEditing ? 'Save changes' : 'Submit'}
        </button>
      </div>
    </form>
  )
}
